import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { MessageCircle, Send, CornerDownRight, ShieldCheck } from "lucide-react";
import {
  fetchSessionDiscussion,
  postSessionDiscussion,
} from "../../../utils/smartLearning";
import { getCurrentUser } from "../../../utils/auth";

const STAFF_TYPES = ["instructor", "admin", "superadmin", "tenantadmin"];

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const then = new Date(dateStr).getTime();
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function initials(name) {
  if (!name) return "?";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");
}

/**
 * SessionDiscussionTab — lightweight per-session Q&A thread.
 *
 * Students post questions; trainers/admins reply. Flat list with one level of
 * threading (parentId). Future-ready: reactions, resolved-state, attachments
 * can be layered on without changing the data contract.
 */
const SessionDiscussionTab = ({ sessionId }) => {
  const me = getCurrentUser();
  const myStudentId = me?.studentId;

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState(null); // parent message or null
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError("");
    try {
      const list = await fetchSessionDiscussion(sessionId);
      setMessages(list);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load the discussion.");
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  // Group into top-level questions + their replies, chronological.
  const threads = useMemo(() => {
    const tops = [];
    const repliesByParent = new Map();
    for (const m of messages) {
      if (m.parentId) {
        const key = String(m.parentId);
        if (!repliesByParent.has(key)) repliesByParent.set(key, []);
        repliesByParent.get(key).push(m);
      } else {
        tops.push(m);
      }
    }
    return tops.map((t) => ({
      ...t,
      replies: repliesByParent.get(String(t._id)) || [],
    }));
  }, [messages]);

  const handleSend = async () => {
    const text = body.trim();
    if (!text || posting) return;
    setPosting(true);
    setError("");
    try {
      const msg = await postSessionDiscussion(sessionId, {
        body: text,
        parentId: replyTo?._id || null,
      });
      if (msg) setMessages((prev) => [...prev, msg]);
      setBody("");
      setReplyTo(null);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to post your message.");
    } finally {
      setPosting(false);
    }
  };

  const renderMessage = (m, isReply = false) => {
    const isStaff = STAFF_TYPES.includes(m.authorType);
    const isOwn = m.authorType === "student" && myStudentId && String(m.authorId) === String(myStudentId);
    return (
      <div
        key={m._id}
        className={`ssd-msg${isReply ? " ssd-msg--reply" : ""}${isStaff ? " ssd-msg--staff" : ""}`}
      >
        <div className={`ssd-msg__avatar${isStaff ? " ssd-msg__avatar--staff" : ""}`}>
          {initials(m.authorName)}
        </div>
        <div className="ssd-msg__body">
          <div className="ssd-msg__head">
            <span className="ssd-msg__author">
              {isOwn ? "You" : m.authorName}
            </span>
            {isStaff && (
              <span className="ssd-msg__role">
                <ShieldCheck size={11} /> Trainer
              </span>
            )}
            <span className="ssd-msg__time">{timeAgo(m.createdAt)}</span>
          </div>
          <p className="ssd-msg__text">{m.body}</p>
          {!isReply && (
            <button
              type="button"
              className="ssd-msg__reply-btn"
              onClick={() => {
                setReplyTo(m);
              }}
            >
              <CornerDownRight size={12} /> Reply
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="ssd-loading">
        <Spinner animation="border" size="sm" />
        <span>Loading discussion…</span>
      </div>
    );
  }

  return (
    <div className="ssd-root">
      {threads.length === 0 ? (
        <div className="ssd-empty">
          <MessageCircle size={26} />
          <h4>No questions yet</h4>
          <p>Be the first to ask a question about this session.</p>
        </div>
      ) : (
        <div className="ssd-thread">
          {threads.map((t) => (
            <div key={t._id} className="ssd-thread__item">
              {renderMessage(t)}
              {t.replies.length > 0 && (
                <div className="ssd-replies">
                  {t.replies.map((r) => renderMessage(r, true))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {error && <div className="ssd-error">{error}</div>}

      {/* Composer */}
      <div className="ssd-composer">
        {replyTo && (
          <div className="ssd-composer__replying">
            <CornerDownRight size={13} />
            Replying to <strong>{replyTo.authorName}</strong>
            <button type="button" onClick={() => setReplyTo(null)} aria-label="Cancel reply">
              ✕
            </button>
          </div>
        )}
        <textarea
          className="ssd-composer__input"
          placeholder={replyTo ? "Write a reply…" : "Ask a question about this session…"}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSend();
          }}
        />
        <div className="ssd-composer__actions">
          <span className="ssd-composer__hint">⌘/Ctrl + Enter to send</span>
          <button
            type="button"
            className="ssd-composer__send"
            onClick={handleSend}
            disabled={!body.trim() || posting}
          >
            {posting ? <Spinner animation="border" size="sm" /> : <Send size={14} />}
            {replyTo ? "Reply" : "Post"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionDiscussionTab;
